import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IRefObject, SearchBox, Text } from '@fluentui/react';
import { BasicHeader } from '../../../actions/BasicHeader';
import AllocateWithDraftSave from '../../../graphql/mutations/sales/SalesOrderAllocateWithDraftSaveMutation.graphql';
import BatchAllocationDefaultQuery from '../../../graphql/queries/product/ProductBatchAllocationQuery.graphql';
import ProductLotAttributesQuery from '../../../graphql/queries/product/ProductLotAttributes.graphql';
import { useMutation, useQuery } from '@apollo/client';
import { useComponentQueryErrorHandling } from '../../../utils/parseErrorResponse';
import { ModalBottomButtonContainer } from '../../../components/modals/Styles';
import { Button, PrimaryButton, SecondaryButton, SuccessButton } from '../../../components/connectedForm/Button';
import ModalContext from '../../../components/Context/ModalContext';
import {
  AlertType,
  FilterOperation,
  IAllocateWithDraftSaveMutationResult,
  IAllocateWithDraftSaveMutationVariables,
  IAllocationSalesHelperContainerInput,
  IAllocationSalesHelperStock,
  IAllocationsQueryResult,
  IAllocationsQueryVariables,
  IBatchAllocationSalesHelperContainerQueryResult,
  IBatchAllocationSalesHelperContainerQueryVariables,
  IEntityMetaData,
  IFilterInput,
  ILotAttributeConfig,
  IProductLotAttributesQueryResult,
  IProductLotAttributesQueryVariables,
  ISalesOrder,
  ISalesOrderInput,
  ISalesOrderLineInput,
  IStockLot,
  IStockLotInput,
  IStockLotsQueryResult,
  IStockLotsQueryVariables,
  LineActionRequired,
  Maybe,
  SalesOrderLineStatus,
} from '../../../graphql/schema/p4types';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import getClientMutationId from '../../../utils/getClientMutationId';
import normalizeVariables, { normalizeFields } from '../../../graphql/schema/normalizeVariables';
import { initialize } from '../../../components/connectedForm/utils/actions';
import { useDispatch } from 'react-redux';
import SalesOrder from '../../../graphql/entities/sales/SalesOrder';
import { submitSalesOrderValues } from './salesOrderFormUtils';
import { serviceActions } from '../../../state/modules/service';
import { IExtendedMetaAction } from './useSalesOrderPostSave';
import SortableDataGrid from '../../../components/EditableDataGrid/SortableDataGrid';
import StockLot from '../../../graphql/entities/product/StockLot';
import { DocumentNode } from 'graphql';
import { ITextField, TextField } from '@fluentui/react/lib/TextField';
import Allocation from '../../../graphql/entities/product/Allocation';
import { IRowUpdatedFunction } from '../../../components/EditableDataGrid/EditableDataGrid';
// @ts-ignore
import debounce from 'es6-promise-debounce';
import { getLotAttributeRowMetaData } from '../../../utils/LotAttributeValues';
import LotAttributeWorkspaceFormatter from '../../../components/grid-addons/formatters/LotAttributeWorkspaceFormatter';
import { IEditableDataGridColumnDef } from '../../../@types/editableDataGridTypes';
import TradeCounterContext from '../../../components/Context/TradeCounterContext';
import { useProcessAlerts } from '../../../utils/EntityMetaDataUtil';
import { cloneDeep } from '@apollo/client/utilities';
import { client } from '../../../client';
import FluentModal from '../../../components/modals/FluentModal';
import { useAllPagesQuery } from '../../../graphql/containers/useAllPagesQuery';
import AutomaticDispatchResultWorkspaceQuery from '../../../graphql/queries/product/AutomaticDispatchResultWorkspaceQuery.graphql';

//UI Helper type - Basic grid data is a StockLot and we've added allocation as a modifiable column, on submit expect to convert to
//               - an IAllocationSalesHelperContainer and/or Array<IAllocationSalesHelperStock>.
type IStockLotAllocation = IStockLot & { allocation: number; persistedAllocation: number };

/**
 * Get a filter function for a SalesOrderLine that indicates whether the line should be batch allocated at this time.
 * @param postSave - Whether the modal is being presented in postSave (true) or line entry (false/undefined).
 */
const getFilterOrderLines = (postSave?: boolean) => {
  return (line: ISalesOrderLineInput) => {
    //Validate that the line is still awaiting allocation or allocated (Since we can de-allocate via this modal).
    //    hence the length === 0 check.
    const status = line.salesOrderLineStatus || [];
    const isNotBeyondAllocated =
      status.length === 0 ||
      status.some(
        (stat) =>
          stat === SalesOrderLineStatus.AwaitingAllocation ||
          stat === SalesOrderLineStatus.Allocated ||
          stat === SalesOrderLineStatus.InProduction ||
          stat === SalesOrderLineStatus.Assigned ||
          stat === SalesOrderLineStatus.AssignedShortage ||
          stat === SalesOrderLineStatus.AssignedAssembly
      );
    const allocationRequired =
      status.length === 0 || status.some((stat) => stat === SalesOrderLineStatus.AwaitingAllocation);

    let required: boolean;
    if (postSave) {
      required = allocationRequired && line.batchAllocationRequired !== LineActionRequired.None;
    } else {
      required = isNotBeyondAllocated;
    }

    return line.product?.batchAllocation && required;
  };
};

const deepContains = (object: any, searchField: string): boolean =>
  Object.keys(object).some((key) => {
    //Ignore uuid & datetime fields, too likely to give false positives and confuse the user.
    if (['uuid', 'updatedAt', 'createdAt', 'id', 'concurrencyVersion'].includes(key)) return false;
    const value = object[key];
    if (value && typeof value === 'object') {
      return deepContains(value, searchField);
    }
    return `${value}`.toLowerCase().indexOf(searchField.toLowerCase()) >= 0;
  });

/**
 * Batch Allocation Modal - Use for both:
 *  - Sales Order Line Batch Allocation (Initial quantity entry, right click \> batch allocation)
 *  - Sales Order Post Save Batch Allocation
 *
 * @param modalOpen   - Whether the modal is currently open.
 * @param metaAction  - MetaData that adjusts the initial selected line in the modal, or indicates it's been triggered by post save.
 * @param tradeCounter - If this is mounted in the Trade Counter (Some behaviours vary, i.e. a form uses redux for values, TC uses state).
 */
const BatchAllocationModal = ({
  modalOpen,
  metaAction,
  tradeCounter,
}: {
  modalOpen: boolean;
  metaAction?: IExtendedMetaAction;
  tradeCounter?: boolean;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const { onClose } = useContext(ModalContext);
  const dispatch = useDispatch();
  const values: ISalesOrderInput = connectedForm.values as any;

  const [initialBatchAllocationLines, initialBatchAllocationIndex] = useMemo(() => {
    const orderLines = values.orderLines;
    const filteredLines =
      (values.orderLines?.filter(getFilterOrderLines(metaAction?.postSave) as any) as Array<ISalesOrderLineInput>) ||
      [];

    let initialIndex = 0;
    if (metaAction?.options?.rowIdx && metaAction.options.rowIdx > 0) {
      const selectedLine = orderLines?.[metaAction.options.rowIdx];
      initialIndex = filteredLines.findIndex((value) => value.uuid === selectedLine?.uuid);
      if (initialIndex < 0) {
        //This shouldn't be a typical/normal occurrence, but if the user manages to do something like initiate batch allocation, then quickly
        //switch the selected row state to a non-batch row, then we need to handle that.
        serviceActions.genericAlert(
          'Batch Allocation',
          'Selected line could not be loaded, loading first line for batch allocation instead.'
        );
        initialIndex = 0;
      }
    }

    return [filteredLines, initialIndex];
  }, [values.orderLines, metaAction]);

  const [batchAllocationLines, setBatchAllocationLines] =
    useState<Array<ISalesOrderLineInput>>(initialBatchAllocationLines);
  const [currentBatchAllocationLineIndex, setCurrentBatchAllocationLineIndex] = useState(0);
  const [searchField, setSearchField] = useState('');
  //Map of stockLotId to target allocation quantity
  // (Which may be more or less than the current allocation quantity for the line).
  const [newAllocationsMap, setNewAllocationsMap] = useState<{
    [lineUuid: string]: {
      [stockLotId: number]: {
        stockLot: IStockLotInput;
        allocation: number;
      };
    };
  }>({});

  const onBatchAllocationModalClose = useCallback(
    (resultData?: Maybe<ISalesOrder>) => {
      //Explicitly clear the new allocations map on close (Since we use the same modal for each line/end-of-order processing).
      setNewAllocationsMap({});
      onClose?.(resultData);
    },
    [onClose]
  );

  //If the modal is mounted/re-mounted, reset batch allocation lines.
  useEffect(() => {
    if (initialBatchAllocationLines.length === 0) {
      onBatchAllocationModalClose?.();
    }
    setCurrentBatchAllocationLineIndex(initialBatchAllocationIndex);
    setBatchAllocationLines(initialBatchAllocationLines);
  }, [onBatchAllocationModalClose, initialBatchAllocationLines, initialBatchAllocationIndex]);

  const handleQueryError = useComponentQueryErrorHandling();
  const [allocateWithDraftSave, { loading }] = useMutation<
    IAllocateWithDraftSaveMutationResult,
    IAllocateWithDraftSaveMutationVariables
  >(AllocateWithDraftSave, {
    onError: handleQueryError,
  });

  const { data: allocationSalesHelperContainerDefault } = useQuery<
    IBatchAllocationSalesHelperContainerQueryResult,
    IBatchAllocationSalesHelperContainerQueryVariables
  >(BatchAllocationDefaultQuery, {
    variables: { salesOrder: normalizeVariables(values) },
    onError: handleQueryError,
  });

  const tradeCounterContext = useContext(TradeCounterContext);

  const processAlerts = useProcessAlerts();
  const handleAllocateWithDraftSave = () => {
    //Perform typical pre-save step before saves, which removes temporary field values/etc.
    const salesOrder = submitSalesOrderValues({ values }, false);
    if (!allocationSalesHelperContainerDefault) {
      throw new Error('Unable to load default allocation helper');
    }

    const allocationSalesHelperContainer = cloneDeep(
      allocationSalesHelperContainerDefault.data
    ) as IAllocationSalesHelperContainerInput;
    //Update the allocationSalesHelperContainer with the updated allocations.
    allocationSalesHelperContainer.allocationSalesHelper?.forEach((allocationSalesHelper) => {
      const newAllocationsByStockLotId = newAllocationsMap[allocationSalesHelper!.salesOrderLine!.uuid!];

      if (newAllocationsByStockLotId && Object.keys(newAllocationsByStockLotId).length > 0) {
        Object.keys(newAllocationsByStockLotId).forEach((stockLotId) => {
          const newAllocationDetail = newAllocationsByStockLotId[stockLotId as any]!;
          const existingAllocationSalesHelperStockIndex = allocationSalesHelper!.allocationSalesHelperStock!.findIndex(
            (ashs) => `${ashs?.stockLot?.id}` === stockLotId
          );
          if (existingAllocationSalesHelperStockIndex >= 0) {
            allocationSalesHelper!.allocationSalesHelperStock![existingAllocationSalesHelperStockIndex] = {
              ...allocationSalesHelper!.allocationSalesHelperStock![existingAllocationSalesHelperStockIndex],
              allocation:
                newAllocationDetail.allocation -
                (newAllocationDetail.stockLot as IStockLotAllocation).persistedAllocation,
            };
          } else {
            allocationSalesHelper!.allocationSalesHelperStock!.push({
              product: allocationSalesHelper!.salesOrderLine!.product,
              stockLot: normalizeVariables(newAllocationDetail.stockLot, [
                'allocation',
                'persistedAllocation',
                'consumed',
              ]),
              allocation:
                newAllocationDetail.allocation -
                (newAllocationDetail.stockLot as IStockLotAllocation).persistedAllocation,
            });
          }
        });
      }
    });

    //Pre-existing 'allocationSalesHelperStock' records have 2 'getter-only' fields that we need to trim, or the mutation will fail.
    //We can't use 'normalizeVariables' because the same field names exist under a child record that shouldn't be removed.
    allocationSalesHelperContainer.allocationSalesHelper?.forEach((ash) =>
      ash?.allocationSalesHelperStock?.forEach((ashs) => {
        delete (ashs as IAllocationSalesHelperStock).binLocation;
        delete (ashs as IAllocationSalesHelperStock).lotAttributes;
      })
    );

    allocationSalesHelperContainer.salesOrder = salesOrder;

    allocateWithDraftSave({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          allocationSalesHelperContainer: normalizeVariables(allocationSalesHelperContainer, [
            ...normalizeFields,
            'levelValuation',
            'allocationValuation',
            'freeStock',
          ]),
          // modal first.
        },
      },
    }).then((result) => {
      const resultData = result?.data?.allocateWithDraftSave?.result;
      if (resultData) {
        if (tradeCounter) {
          tradeCounterContext.updateSalesOrder!('', resultData);
        } else {
          dispatch(
            initialize(SalesOrder.type, result.data?.allocateWithDraftSave?.result, connectedForm.fieldValidation)
          );
        }

        processAlerts(resultData.entityMetaData! as IEntityMetaData, 'Draft Save');
        dispatch(serviceActions.genericAlert('Draft Save', 'Draft save has been completed', AlertType.Success));
        onBatchAllocationModalClose?.(resultData as ISalesOrder);
        //Force refetches on batch allocation modal queries because the allocation will have made them invalid (Out of date).
        client.refetchQueries({ include: [BatchAllocationDefaultQuery, Allocation.workspace!] });
      }
    });
  };

  const currentBatchAllocationLine: Maybe<ISalesOrderLineInput> = batchAllocationLines[currentBatchAllocationLineIndex];

  const onClickSkip = () => {
    if (tradeCounter) {
      const updatedOrderLines = values.orderLines!.map((orderLine) => {
        if (orderLine?.batchAllocationRequired === LineActionRequired.Any) {
          //If this is a line-triggered batchAllocation, set lines to 'AfterSave' as we've selected 'Add Later'.
          //If this is pre-save, then set to 'none' as the trade counter will auto-allocate.
          return {
            ...orderLine,
            batchAllocationRequired: metaAction?.preSave ? LineActionRequired.None : LineActionRequired.AfterSave,
          };
        }
        return orderLine;
      });

      tradeCounterContext.updateSalesOrder!('orderLines', updatedOrderLines);
    } else {
      values.orderLines?.forEach((value, index) => {
        //Tag that the user has specified to process later (after save) so that we don't present them this modal again until then.
        if (value?.batchAllocationRequired === LineActionRequired.Any) {
          dispatch(connectedForm.change?.(`orderLines.${index}.batchAllocationRequired`, LineActionRequired.AfterSave));
        }
      });
    }

    onBatchAllocationModalClose?.();
  };

  const onClickPreviousProduct = () => {
    setCurrentBatchAllocationLineIndex((value) => (value > 0 ? value - 1 : 0));
  };

  const onClickNextUnAllocatedProduct = () => {
    setCurrentBatchAllocationLineIndex((value) => value + 1);
  };

  const stockLotVariables = useMemo<IStockLotsQueryVariables>(() => {
    const filters = [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: { field: 'stock.product.id', value: currentBatchAllocationLine?.product?.id },
      },
      {
        filterOperation: FilterOperation.GreaterThan,
        filterParameters: { field: 'level', value: '0' },
      },
    ];

    const warehouseid = currentBatchAllocationLine?.warehouse?.id;
    if (warehouseid) {
      filters.push({
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'stock.binLocation.warehouse.id',
          value: warehouseid,
        },
      });
    }
    return {
      filters: filters,
      first: 200,
      excludeBinLocation: false,
      excludeProduct: true,
    };
  }, [currentBatchAllocationLine?.product?.id, currentBatchAllocationLine?.warehouse?.id]);

  const [fetchAllStockLots, { data: stockLotsWorkspaceData }] = useAllPagesQuery<
    IStockLotsQueryResult,
    IStockLotsQueryVariables
  >(StockLot.workspace!);

  useEffect(() => {
    if (currentBatchAllocationLine) {
      fetchAllStockLots(stockLotVariables);
    }
  }, [currentBatchAllocationLine, stockLotVariables, fetchAllStockLots]);

  const allocationFilters: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'orderLine.id',
        value: currentBatchAllocationLine?.id,
      },
    },
  ];

  //Fetch detail on any existing allocations for the line.
  const { data: existingAllocationData } = useQuery<IAllocationsQueryResult, IAllocationsQueryVariables>(
    Allocation.workspace as DocumentNode,
    {
      variables: { filters: allocationFilters, first: 200 },
      skip: !currentBatchAllocationLine, //There should always be a line, but covering potential edge case during mount.
      fetchPolicy: 'network-only',
      onError: handleQueryError,
    }
  );

  const stockLots = useMemo(
    () => stockLotsWorkspaceData?.listData?.edges?.map((edge) => edge!.node) || [],
    [stockLotsWorkspaceData]
  );

  const allocations = useMemo(
    () => existingAllocationData?.listData?.edges?.map((edge) => edge!.node) || [],
    [existingAllocationData]
  );

  //Build the grid data from the list of stock lots and the list of allocations.
  const stockLotGridData = useMemo<Array<IStockLotAllocation>>(() => {
    if (!currentBatchAllocationLine) return [];
    return stockLots.map((stockLot) => {
      //Get the user requested allocation, if they have made edits.
      const targetAllocation = (newAllocationsMap[currentBatchAllocationLine.uuid!] || {})[stockLot!.id]?.allocation;
      //Get the currently persisted allocation amount for this sales order line.
      const persistedAllocation = allocations
        .filter((allocation) => allocation?.stockLot?.id === stockLot?.id)
        .map((value) => value!.allocated)
        .reduce((a, b) => a + b, 0);
      const currentlyAllocated = targetAllocation !== undefined ? targetAllocation : persistedAllocation;

      return {
        ...(stockLot as IStockLot),
        persistedAllocation: persistedAllocation,
        allocation: currentlyAllocated,
      };
    });
  }, [stockLots, allocations, newAllocationsMap, currentBatchAllocationLine]);

  //Local filtering - Quite limited, alternatively could work on remote filtering, but then we need a way to do an OR filter
  const filteredStockLotGridData = useMemo(() => {
    return stockLotGridData.filter((value) => {
      return value.allocation > 0 || searchField.length === 0 || deepContains(value, searchField);
    });
  }, [stockLotGridData, searchField]);

  const getPrecision = useCallback(() => currentBatchAllocationLine?.product?.precision, [currentBatchAllocationLine]);

  const { data: productLotAttributesData } = useQuery<
    IProductLotAttributesQueryResult,
    IProductLotAttributesQueryVariables
  >(ProductLotAttributesQuery, {
    variables: { id: currentBatchAllocationLine?.product?.id },
    skip: !currentBatchAllocationLine?.product?.id,
  });

  const stockLotColumnMeta: Array<IEditableDataGridColumnDef> = useMemo(() => {
    const nonReplenishableColumns: Array<IEditableDataGridColumnDef> = currentBatchAllocationLine?.product
      ?.nonReplenishable
      ? [
          {
            name: 'Original Level',
            key: 'originalLevel',
            editable: false,
            resizable: true,
            formatter: 'numeric',
          },
          {
            name: 'Consumed Stock',
            key: 'consumed',
            editable: false,
            resizable: true,
            formatter: 'numeric',
          },
        ]
      : [];

    const lotAttributes = productLotAttributesData?.data?.lotAttributes || [];
    const lotAttributeColumns: Array<IEditableDataGridColumnDef> = (lotAttributes as Array<ILotAttributeConfig>).map(
      (attr) => {
        return {
          name: attr.title,
          key: 'lotAttributeValues',
          sortable: false,
          editable: false,
          formatter: LotAttributeWorkspaceFormatter,
          options: { attributeConfig: attr, valuesPath: 'lotAttributeValues' },
          getRowMetaData: getLotAttributeRowMetaData,
        };
      }
    );

    return [
      ...lotAttributeColumns,
      ...nonReplenishableColumns,
      {
        name: 'Level',
        key: 'level',
        editable: false,
        resizable: true,
        formatter: 'numeric',
      },
      {
        name: 'Location',
        key: 'stock.binLocation.location',
        editable: false,
        resizable: true,
      },
      {
        name: 'Free Stock',
        key: 'freeStock',
        editable: false,
        resizable: true,
        formatter: 'numeric',
      },
      {
        name: 'Allocated',
        key: 'allocated',
        editable: false,
        resizable: true,
        formatter: 'numeric',
      },
      {
        name: 'Issued',
        key: 'issued',
        editable: false,
        resizable: true,
        formatter: 'numeric',
      },
      {
        name: 'Allocation',
        key: 'allocation',
        editable: true,
        resizable: true,
        formatter: 'numeric',
        options: {
          format: { decimals: getPrecision },
        },
      },
    ];
  }, [
    currentBatchAllocationLine?.product?.nonReplenishable,
    getPrecision,
    productLotAttributesData?.data?.lotAttributes,
  ]);

  //Locally tracking state for better UX, but ultimately will be reset to the connectedForm value.
  const [quantity, setQuantity] = useState(currentBatchAllocationLine?.quantity ?? 0);
  //Depending on whether the line or quantity was changed, we need a different pacing of state update.
  const [lineUuidForQuantityUpdate, setLineUuidForQuantityUpdate] = useState(currentBatchAllocationLine?.uuid);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedQuantityRefresh = useCallback(
    debounce((updatedQuantity: number) => {
      setQuantity(updatedQuantity);
    }, 2000),
    [setQuantity]
  );

  //If the selected line changes, reset the quantity.
  useEffect(() => {
    if (currentBatchAllocationLine?.quantity === undefined) return;
    if (currentBatchAllocationLine.uuid === lineUuidForQuantityUpdate) {
      //Quantity only was updated, debounce it to keep the user input small.
      debouncedQuantityRefresh(currentBatchAllocationLine.quantity);
    } else {
      //The user has switched to a different line, immediately update the quantity field.
      setQuantity(currentBatchAllocationLine.quantity);
      setLineUuidForQuantityUpdate(currentBatchAllocationLine.uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentBatchAllocationLine,
    currentBatchAllocationLine?.uuid,
    currentBatchAllocationLine?.quantity,
    setQuantity,
    debouncedQuantityRefresh,
  ]);

  const quantityRef = useRef<ITextField>();
  useEffect(() => {
    quantityRef.current?.select?.();
  }, []);

  const onRowUpdated: IRowUpdatedFunction<IStockLotAllocation> = (commit) => {
    if (commit.cellKey !== 'allocation') {
      throw new Error('Only the allocation column is expected to be editable here.');
    } else if (!currentBatchAllocationLine) {
      throw new Error('No current batch allocation line, unable to update');
    }

    let newAllocation: number = commit.updated?.allocation || 0;
    const maximumAvailableInStockLot: number =
      (commit.original?.freeStock ?? 0) + (commit.original?.persistedAllocation ?? 0);
    const maximumRequestedForLine: number = quantity;

    //Note - Doing some basic validation here for speed, but it is imperfect (Doesn't check other lines with the same product, for example).
    //     - However, services should catch those and error. This is just to give the user some faster feedback.
    if (newAllocation < 0) {
      newAllocation = 0;
      dispatch(
        serviceActions.genericAlert('Invalid amount', 'Allocation quantities cannot be negative, allocation set to 0')
      );
    } else if (newAllocation > maximumRequestedForLine) {
      newAllocation = maximumRequestedForLine;
      dispatch(
        serviceActions.genericAlert(
          'Invalid amount',
          'More requested than line quantity, allocation reduced to line quantity'
        )
      );
    } else if (newAllocation > maximumAvailableInStockLot) {
      newAllocation = maximumAvailableInStockLot;
      dispatch(
        serviceActions.genericAlert('Invalid amount', 'Not enough free stock, allocation reduced to maximum amount')
      );
    }

    setNewAllocationsMap((allocationsMap) => {
      const existingLotAllocations = allocationsMap[currentBatchAllocationLine.uuid!] || {};

      return {
        ...allocationsMap,
        [currentBatchAllocationLine.uuid!]: {
          ...existingLotAllocations,
          [commit.original!.id]: {
            stockLot: commit.original as IStockLotInput,
            allocation: newAllocation,
          },
        },
      };
    });
  };

  const onQuantityChange = (_: any, newValue: string) => {
    setQuantity(newValue);

    const lineIndex = (connectedForm.values as ISalesOrderInput).orderLines!.findIndex(
      (orderLine) => orderLine!.uuid === currentBatchAllocationLine.uuid
    );

    if (tradeCounter) {
      tradeCounterContext.updateSalesOrder!(['orderLines', lineIndex, 'quantity'], +newValue);
    } else {
      //Immediately update quantity in form values.
      //Note that this will trigger a useEffect which will do a debounced update back to quantity to keep the fields in sync.
      dispatch(connectedForm.change(`orderLines.${lineIndex}.quantity`, +newValue));
    }
  };

  return (
    <FluentModal isOpen={modalOpen} styles={{ main: { minWidth: window.innerWidth * 0.8, paddingBottom: '60px' } }}>
      <BasicHeader
        secondaryTitle={'Batch product added to order - Please select lots'}
        tertiaryTitle={`(${currentBatchAllocationLine?.product?.description})`}
        styles={{ secondary: { color: '#3949AB' } }}
      >
        {currentBatchAllocationLine?.product?.reference} Batch Allocation
      </BasicHeader>
      <div style={{ padding: '1em' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <TextField
            type="number"
            componentRef={quantityRef as IRefObject<ITextField>}
            value={quantity}
            onClick={(e) => {
              e.stopPropagation();
            }}
            autoFocus={true}
            underlined={true}
            data-testid={`batch-allocation-quantity`}
            label={'Quantity'}
            onChange={onQuantityChange as any}
          />
          <SearchBox
            placeholder={'Search'}
            underlined={true}
            data-testid={`batch-allocation-search`}
            styles={{ root: { marginTop: '1px', marginLeft: '5px' } }}
            value={searchField}
            onChange={(_, value) => {
              setSearchField(value || '');
            }}
          />
          <Button
            style={{ marginLeft: 'auto' }}
            onClick={onClickPreviousProduct}
            disabled={currentBatchAllocationLineIndex <= 0}
          >{`< Previous Product`}</Button>
          <Text style={{ alignSelf: 'center', marginLeft: '5px', marginRight: '5px' }}>{`Product ${
            currentBatchAllocationLineIndex + 1
          } of ${batchAllocationLines.length}`}</Text>
          <SuccessButton
            onClick={onClickNextUnAllocatedProduct}
            disabled={currentBatchAllocationLineIndex + 1 >= batchAllocationLines.length}
          >
            Next Unallocated
          </SuccessButton>
        </div>
        <SortableDataGrid
          listData={filteredStockLotGridData}
          rowsCount={filteredStockLotGridData.length}
          columns={stockLotColumnMeta}
          enableCellSelect={true}
          addBlankRow={false}
          autoContentHeight={false}
          filterable={false}
          contentHeight={window.innerHeight * 0.7}
          contentWidth={1000}
          onRowUpdated={onRowUpdated}
        />
      </div>
      <ModalBottomButtonContainer>
        <SecondaryButton onClick={onClickSkip}>
          {metaAction?.postSave || metaAction?.preSave ? 'Skip' : 'Add Later'}
        </SecondaryButton>
        <PrimaryButton onClick={handleAllocateWithDraftSave} disabled={loading}>
          {metaAction?.preSave ? 'Complete Order' : 'Save and Allocate'}
        </PrimaryButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default BatchAllocationModal;
